import { Injectable } from '@angular/core';
import { Firestore, collection, doc, getCountFromServer, query, where, orderBy, limit as firestoreLimit, docData, collectionData, getDoc, deleteDoc } from '@angular/fire/firestore';
import { Observable } from 'rxjs';
import { Vol } from '../models/vol.interface';
import { Image } from '../models/image.interface';
import { User } from '../models/user.interface';

@Injectable({
  providedIn: 'root',
})
export class VolService {
  constructor(private firestore: Firestore) {}

  getVolList(uid: string): Observable<Vol[]> {
    const volsRef = collection(this.firestore, 'ge_users', uid, 'vols');
    return collectionData(volsRef, { idField: 'id' }) as Observable<Vol[]>;
  }

  getVolDetail(uid: string, id: string): Observable<Vol> {
    const volDoc = doc(this.firestore, `/ge_users/${uid}/vols/${id}`);
    return docData(volDoc, { idField: 'id' }) as Observable<Vol>;
  }

  deleteVol(uid: string, id: string): Promise<void> {
    const volDoc = doc(this.firestore, `/ge_vols/${id}`);
    const volUserDoc = doc(this.firestore, `/ge_users/${uid}/vols/${id}`);
    return deleteDoc(volUserDoc).then((e) => {
      deleteDoc(volDoc);
    });
  }

  getVolGallery(uid: string, id: string): Observable<Image[]> {
    const imagesRef = collection(this.firestore, '/ge_users/' + uid + '/vols/' + id + '/images/');
    const q = query(
      imagesRef,
      orderBy('index', 'desc'),
      firestoreLimit(5)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Image[]>;
  }

  async countClassifiedVols(classification: string, country: string): Promise<number> {
    const collectionRef = collection(this.firestore, 'ge_vols');
    const countQuery = query(collectionRef, where('classification', 'array-contains', classification), where('country', '==', country), where('visibility', '==', true));
    const count = await getCountFromServer(countQuery);
    return count.data().count;
  }

  async newClassifiedVols(classification: string, country: string): Promise<number> {
    const today = Date.now();
    const month = today - 30 * 24 * 60 * 60 * 1000;
    const collectionRef = collection(this.firestore, 'ge_vols');
    const countQuery = query(collectionRef, where('classification', 'array-contains', classification), where('country', '==', country), where('visibility', '==', true), where('publish', '>=', month));
    const count = await getCountFromServer(countQuery);
    return count.data().count;
  }

  async countGlobalVols(classification: string): Promise<number> {
    const collectionRef = collection(this.firestore, 'ge_vols');
    const countQuery = query(collectionRef, where('classification', 'array-contains', classification), where('visibility', '==', true));
    const count = await getCountFromServer(countQuery);
    return count.data().count;
  }

  async newGlobalVols(classification: string): Promise<number> {
    const today = Date.now();
    const month = today - 30 * 24 * 60 * 60 * 1000;
    const collectionRef = collection(this.firestore, 'ge_vols');
    const countQuery = query(collectionRef, where('classification', 'array-contains', classification), where('visibility', '==', true), where('publish', '>=', month));
    const count = await getCountFromServer(countQuery);
    return count.data().count;
  }

  async countIndustryVols(grouping: string, country: string): Promise<number> {
    const collectionRef = collection(this.firestore, 'ge_vols');
    const countQuery = query(collectionRef, where('grouping', 'array-contains', grouping), where('country', '==', country), where('visibility', '==', true));
    const count = await getCountFromServer(countQuery);
    return count.data().count;
  }

  async countCountryVols(country: string): Promise<number> {
    const collectionRef = collection(this.firestore, 'ge_vols');
    const countQuery = query(collectionRef, where('location', '==', 'onsite'), where('country', '==', country), where('visibility', '==', true));
    const count = await getCountFromServer(countQuery);
    return count.data().count;
  }

  async countRemoteVols(): Promise<number> {
    const collectionRef = collection(this.firestore, 'ge_vols');
    const countQuery = query(collectionRef, where('location', '==', 'remote'), where('visibility', '==', true));
    const count = await getCountFromServer(countQuery);
    return count.data().count;
  }

  async countVols(): Promise<number> {
    const collectionRef = collection(this.firestore, 'ge_vols');
    const countQuery = query(collectionRef, where('visibility', '==', true));
    const count = await getCountFromServer(countQuery);
    return count.data().count;
  }

  getCountryVols(country: string, limitValue: number): Observable<Vol[]> {
    const volCollectionRef = collection(this.firestore, 'ge_vols');
    const q = query(volCollectionRef,
      where('country', '==', country),
      where('location', '==', 'onsite'),
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(limitValue)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Vol[]>;
  }

  getLocalVols(location: string, country: string, limitValue: number): Observable<Vol[]> {
    const volCollectionRef = collection(this.firestore, 'ge_vols');
    const q = query(volCollectionRef,
      where('country', '==', country),
      where('cities', 'array-contains', location),
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(limitValue)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Vol[]>;
  }

  getRegionVols(location: string, country: string, limitValue: number): Observable<Vol[]> {
    const volCollectionRef = collection(this.firestore, 'ge_vols');
    const q = query(volCollectionRef,
      where('country', '==', country),
      where('regions', 'array-contains', location),
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(limitValue)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Vol[]>;
  }

  getClassifiedVols(classification: string, country: string, limitValue: number): Observable<Vol[]> {
    const volCollectionRef = collection(this.firestore, 'ge_vols');
    const q = query(volCollectionRef,
      where('classification', 'array-contains', classification),
      where('country', '==', country),
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(limitValue)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Vol[]>;
  }

  getAllClassifiedVols(classification: string, limitValue: number): Observable<Vol[]> {
    const volCollectionRef = collection(this.firestore, 'ge_vols');
    const q = query(volCollectionRef,
      where('classification', 'array-contains', classification),
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(limitValue)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Vol[]>;
  }

  getIndustryVols(grouping: string, country: string, limitValue: number): Observable<Vol[]> {
    const volCollectionRef = collection(this.firestore, 'ge_vols');
    const q = query(volCollectionRef,
      where('grouping', 'array-contains', grouping),
      where('country', '==', country),
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(limitValue)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Vol[]>;
  }

  getAllIndustryVols(grouping: string, limitValue: number): Observable<Vol[]> {
    const volCollectionRef = collection(this.firestore, 'ge_vols');
    const q = query(volCollectionRef,
      where('grouping', 'array-contains', grouping),
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(limitValue)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Vol[]>;
  }

  getRemoteVols(limitValue: number) {
    const volCollectionRef = collection(this.firestore, 'ge_vols');
    const q = query(volCollectionRef,
      where('location', '==', 'remote'),
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(limitValue)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Vol[]>;
  }

  vols() {
    const volCollectionRef = collection(this.firestore, 'ge_vols');
    const q = query(volCollectionRef,
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(10)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Vol[]>;
  }

  getProfile(uid: string) {
    const userRef = doc(this.firestore, `ge_users/${uid}`);
    return getDoc(userRef);
  }

  volCard(id: string) {
    const volRef = doc(this.firestore, `ge_vols/${id}`);
    return getDoc(volRef);
  }

  volDetail(id: string): Observable<Vol> {
    const volDoc = doc(this.firestore, `ge_vols/${id}`);
    return docData(volDoc, { idField: 'id' }) as Observable<Vol>;
  }

  volDetails(slug: string) {
    const q = query(
      collection(this.firestore, 'ge_vols'),
      where('slug', '==', slug)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Vol[]>;
  }

  volunteers(id: string): Observable<User[]> {
    const volunteersRef = collection(this.firestore, `ge_vols/${id}/volunteers`);
    const q = query(
      volunteersRef,
      orderBy('applied', 'desc'),
      firestoreLimit(10)
    );
    return collectionData(q, { idField: 'id' }) as Observable<User[]>;
  }
}
